import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileDown, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { Field, inputCls } from './shared';
import { pollJob, downloadBlob } from '../../utils/aiJobs';

const TEXT = [
  ['plaintiff_name', 'Demandante (madre/padre que representa)', true], ['plaintiff_id', 'RUT/DNI demandante'], ['plaintiff_address', 'Domicilio demandante'],
  ['defendant_name', 'Demandado (alimentante)', true], ['defendant_id', 'RUT/DNI demandado'], ['defendant_job', 'Profesión u oficio del demandado'],
  ['defendant_address', 'Domicilio demandado'], ['city', 'Ciudad del tribunal'], ['court', 'Tribunal (opcional)'], ['lawyer', 'Abogado patrocinante'],
  ['mediation', 'Mediación previa (ej: certificado de mediación frustrada N°...)'],
];

export const DemandDialog = ({ calc, open, onClose }) => {
  const { api } = useAuth();
  const [f, setF] = useState({ facts: '' });
  const [children, setChildren] = useState([{ name: '', birth_date: '' }]);
  const [busy, setBusy] = useState(false);
  const [demandId, setDemandId] = useState(null);

  const download = async (id) => {
    const r = await api.get(`/family/demands/${id}/docx`, { responseType: 'blob' });
    downloadBlob(r.data, 'demanda_alimentos.docx');
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/family/child-support/demand', { calc, ...f, children: children.filter((c) => c.name.trim()) });
      toast.info('El socio de Familia IA está redactando la demanda (1-2 minutos)…');
      const res = await pollJob(api, data.job_id);
      setDemandId(res.demand_id);
      await download(res.demand_id);
      toast.success('Demanda lista. Revísala antes de presentarla.');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'No se pudo redactar la demanda');
    } finally {
      setBusy(false);
    }
  };

  const setChild = (i, k, v) => setChildren(children.map((c, j) => (j === i ? { ...c, [k]: v } : c)));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="demand-dialog" className="max-h-[90vh] overflow-y-auto border-slate-700 bg-slate-900 text-white sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Demanda de alimentos en Word</DialogTitle>
          <DialogDescription className="text-slate-400">La IA redacta hechos, derecho, petitorio y otrosíes con estos datos y el cálculo. Lo que dejes vacío quedará como [completar].</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
          {TEXT.map(([k, label, req]) => (
            <Field key={k} label={label}>
              <input data-testid={`demand-${k}`} required={req} value={f[k] || ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} />
            </Field>
          ))}
          <div className="sm:col-span-2 space-y-2">
            <span className="block text-xs font-medium uppercase tracking-wider text-slate-400">Hijos</span>
            {children.map((c, i) => (
              <div key={i} className="flex gap-2">
                <input data-testid={`demand-child-name-${i}`} placeholder="Nombre completo" value={c.name} onChange={(e) => setChild(i, 'name', e.target.value)} className={inputCls} />
                <input data-testid={`demand-child-birth-${i}`} type="date" value={c.birth_date} onChange={(e) => setChild(i, 'birth_date', e.target.value)} className={`${inputCls} max-w-[170px]`} />
                {children.length > 1 && <button type="button" onClick={() => setChildren(children.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
            <button type="button" data-testid="demand-add-child" onClick={() => setChildren([...children, { name: '', birth_date: '' }])} className="inline-flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200"><Plus className="h-3 w-3" /> Agregar hijo</button>
          </div>
          <div className="sm:col-span-2">
            <Field label="Hechos adicionales (opcional)">
              <textarea data-testid="demand-facts" rows={3} value={f.facts} onChange={(e) => setF({ ...f, facts: e.target.value })} className={inputCls} placeholder="Ej: el demandado no aporta desde marzo; gastos de colegio y salud…" />
            </Field>
          </div>
          <div className="sm:col-span-2 flex flex-wrap justify-end gap-2">
            {demandId && (
              <button type="button" data-testid="demand-redownload" onClick={() => download(demandId)} className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800">
                <FileDown className="h-4 w-4" /> Descargar de nuevo
              </button>
            )}
            <button data-testid="demand-submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-rose-500 px-6 py-2 text-sm font-semibold text-white hover:bg-rose-400 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} {busy ? 'Redactando…' : 'Generar demanda en Word'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
